import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { PaperPlaneTilt } from "@phosphor-icons/react";

export default function SendMoney() {
  const qc = useQueryClient();
  const { data: wallet } = useQuery({ queryKey: ["wallet"], queryFn: async () => (await api.get("/wallet")).data });
  const { data: settings } = useQuery({ queryKey: ["public-settings"], queryFn: async () => (await api.get("/settings/public")).data });
  const [recipient, setRecipient] = useState("");
  const [amount, setAmount] = useState(500);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const feePct = Number(settings?.transfer_fee_pct ?? 0);
  const feeFlat = Number(settings?.transfer_fee_flat_cents ?? 0);
  const feeCents = Math.round((amount * feePct) / 100) + feeFlat;
  const total = amount + feeCents;
  const feeLabel = feePct > 0 && feeFlat > 0 ? `${feePct}% + ${money(feeFlat)}` : feePct > 0 ? `${feePct}%` : feeFlat > 0 ? money(feeFlat) : "Free";

  const send = async () => {
    if (!recipient.trim()) { toast.error("Enter recipient phone or email"); return; }
    if (amount < 100) { toast.error("Minimum transfer is $1.00"); return; }
    setBusy(true); setDone(null);
    try {
      const { data } = await api.post("/wallet/transfer", { recipient: recipient.trim(), amount_cents: amount, note });
      setDone(data);
      toast.success(`Sent ${money(data.amount_cents)} to ${data.recipient}`);
      setRecipient(""); setNote("");
      qc.invalidateQueries({ queryKey: ["wallet"] });
      qc.invalidateQueries({ queryKey: ["ledger"] });
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="send-money-page">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Send Money</h1>
        <p className="mt-2 text-muted-foreground">Instantly send wallet credit to another iYeneh member by phone or email.</p>
      </div>

      <div className="mx-auto max-w-md rounded-2xl border border-border bg-card p-5">
        <div className="mb-4 flex items-center justify-between rounded-xl bg-secondary px-4 py-3 text-sm">
          <span className="text-muted-foreground">Your balance</span>
          <span data-testid="sm-balance" className="font-mono-metric font-semibold">{wallet ? money(wallet.balance_cents, wallet.currency) : "—"}</span>
        </div>

        <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Recipient (phone or email)</label>
        <input data-testid="sm-recipient" value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="+237 6XX XXX XXX or name@email.com"
          className="mt-1.5 w-full rounded-xl border border-input bg-background px-3 py-2.5 outline-none focus:border-primary" />

        <label className="mt-3 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Amount</label>
        <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-input px-3">
          <span className="text-muted-foreground">$</span>
          <input data-testid="sm-amount" type="number" min="1" value={(amount / 100).toString()}
            onChange={(e) => setAmount(Math.round(Number(e.target.value) * 100))}
            className="w-full bg-transparent py-2.5 outline-none" />
        </div>

        <label className="mt-3 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Note (optional)</label>
        <input data-testid="sm-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={140} placeholder="What's it for?"
          className="mt-1.5 w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm outline-none focus:border-primary" />

        <div className="mt-4 space-y-1 rounded-xl bg-secondary p-3 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Amount</span><span className="font-mono-metric">{money(amount)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Transaction fee ({feeLabel})</span><span data-testid="sm-fee" className="font-mono-metric">{money(feeCents)}</span></div>
          <div className="flex justify-between border-t border-border pt-1 font-semibold"><span>Total debited</span><span data-testid="sm-total" className="font-mono-metric">{money(total)}</span></div>
        </div>

        <button data-testid="sm-send" onClick={send} disabled={busy}
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3.5 font-semibold text-primary-foreground transition-transform hover:scale-[1.02] disabled:opacity-60">
          <PaperPlaneTilt size={18} weight="fill" /> {busy ? "Sending…" : `Send ${money(amount)}`}
        </button>
        <p className="mt-2 text-center text-xs text-muted-foreground">Sent from your added (paid) funds. Fee is non-refundable.</p>

        {done && (
          <div data-testid="sm-done" className="mt-4 rounded-xl border border-emerald-300 bg-emerald-50/60 p-3 text-sm text-emerald-800 dark:bg-emerald-950/30">
            Sent {money(done.amount_cents)} to {done.recipient} (fee {money(done.fee_cents)}).
          </div>
        )}
      </div>
    </div>
  );
}
